<template>
  <v-container>
    <v-card class="mx-auto app-content-card" max-width="600" variant="flat">
      <v-card-title>
        <span class="text-h6">{{ $t("crm.uploadCv.title") }}</span>
      </v-card-title>
      <v-card-text>
        <v-progress-linear v-if="readinessLoading" indeterminate color="primary" class="mb-4" />
        <v-alert
          v-else-if="readiness && !readiness.ready"
          type="warning"
          class="mb-4"
        >
          {{ readinessMessage }}
          <div v-if="showAiSettingsAction" class="mt-2">
            <v-btn variant="text" @click="goToAiSettings">
              {{ $t("crm.uploadCv.openAiSettings") }}
            </v-btn>
          </div>
        </v-alert>

        <v-form ref="form" @submit.prevent="submitForm">
          <v-autocomplete
            v-if="isAdmin"
            :label="$t('candidates.form.company')"
            v-model="selectedCompanyId"
            :items="companies"
            item-title="company_name"
            item-value="id"
            :rules="[(v) => !!v || $t('crm.comments.required')]"
            required
            prepend-icon="mdi-office-building"
          ></v-autocomplete>

          <v-file-input
            :label="$t('crm.uploadCv.fileLabel')"
            accept=".pdf,.txt,.csv,.docx,.xlsx"
            v-model="cvFile"
            required
            prepend-icon="mdi-file-upload"
          ></v-file-input>

          <v-btn color="primary" type="submit" :loading="loading" :disabled="!canSubmit">
            {{ $t("crm.uploadCv.submit") }}
          </v-btn>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn variant="text" @click="goBack">{{ $t("crm.uploadCv.back") }}</v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" location="bottom right">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import { getRolesSetFromStorage } from "@/Utile/navigationDrawerAuth.mjs";

export default {
  data() {
    return {
      cvFile: null,
      loading: false,
      companies: [], // pentru selectorul de companie (doar admin)
      selectedCompanyId: null,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      readiness: null,
      readinessLoading: false,
      readinessRequestId: 0,
    };
  },
  computed: {
    // Admin alege compania pentru candidatul importat din CV; HR/miniadmin iau
    // compania din token (backend-ul o rezolvă în /process-cv).
    isAdmin() {
      return getRolesSetFromStorage().has("admin");
    },
    canSubmit() {
      return Boolean(this.readiness?.ready) && !this.readinessLoading;
    },
    readinessMessage() {
      return this.resolveAiErrorMessage(this.readiness?.error, this.readiness?.message);
    },
    showAiSettingsAction() {
      return ["AI_PROVIDER_CREDENTIAL_REQUIRED", "AI_CREDENTIAL_DECRYPT_FAILED"].includes(
        this.readiness?.error
      );
    },
  },
  created() {
    if (this.isAdmin) {
      this.fetchCompanies();
    } else {
      this.fetchReadiness();
    }
  },
  watch: {
    selectedCompanyId() {
      if (this.isAdmin) this.fetchReadiness();
    },
  },
  methods: {
    fetchCompanies() {
      axios
        .get("/candidati/companies")
        .then((r) => { this.companies = Array.isArray(r.data) ? r.data : []; })
        .catch((e) => console.error("Eroare la fetch companii:", e));
    },
    async fetchReadiness() {
      const requestId = ++this.readinessRequestId;
      this.readiness = null;
      if (this.isAdmin && !this.selectedCompanyId) {
        this.readinessLoading = false;
        return;
      }
      this.readinessLoading = true;
      try {
        const response = await axios.get("/process-cv/readiness", {
          params: this.isAdmin ? { company_id: this.selectedCompanyId } : undefined,
        });
        if (requestId === this.readinessRequestId) this.readiness = response.data;
      } catch (error) {
        if (requestId === this.readinessRequestId) {
          this.readiness = {
            ready: false,
            error: error?.response?.data?.error || "AI_CV_READINESS_FAILED",
            message: error?.response?.data?.message,
          };
        }
      } finally {
        if (requestId === this.readinessRequestId) this.readinessLoading = false;
      }
    },
    async submitForm() {
      if (!this.cvFile) {
        this.notify(this.$t("crm.uploadCv.missingFile"), "error");
        return;
      }
      if (this.isAdmin && !this.selectedCompanyId) {
        this.notify(this.$t("crm.uploadCv.missingCompany"), "warning");
        return;
      }
      if (!this.readiness?.ready) {
        this.notify(this.readinessMessage, "warning");
        return;
      }

      this.loading = true;

      const formData = new FormData();
      formData.append("cv", this.cvFile);
      if (this.isAdmin && this.selectedCompanyId) {
        formData.append("company_id", this.selectedCompanyId);
      }

      try {
        const response = await axios.post(
          "/process-cv",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // Acceptă atât răspuns JSON (nou) cât și text simplu (compatibilitate).
        // Duplicatul este cea mai importantă informație -> un singur snackbar de avertizare;
        // altfel afișăm confirmarea de succes.
        const data = response.data;
        const isDuplicate =
          typeof data === "object" && data !== null && data.duplicate;
        if (isDuplicate) {
          const nume = [data?.name?.first, data?.name?.last].filter(Boolean).join(" ");
          this.notify(this.$t("crm.uploadCv.duplicate", { name: nume || "" }), "warning");
        } else {
          const msg =
            (typeof data === "object" && data !== null && data.message) ||
            this.$t("crm.uploadCv.success");
          this.notify(msg, "success");
        }

        // Resetăm formularul pentru un nou import (refresh implicit, fără buton manual).
        this.cvFile = null;
      } catch (error) {
        const errorCode = error?.response?.data?.error;
        this.notify(
          this.resolveAiErrorMessage(errorCode, error?.response?.data?.message),
          "error"
        );
        if (["AI_PROVIDER_CREDENTIAL_REQUIRED", "AI_CREDENTIAL_DECRYPT_FAILED"].includes(errorCode)) {
          await this.fetchReadiness();
        }
      } finally {
        this.loading = false;
      }
    },
    notify(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    goBack() {
      this.$router.push({ name: "candidati" }); // Asigură-te că ruta există
    },
    goToAiSettings() {
      this.$router.push({
        name: "AiWorkspace",
        query: {
          tab: "settings",
          ...(this.selectedCompanyId ? { companyId: this.selectedCompanyId } : {}),
        },
      });
    },
    resolveAiErrorMessage(errorCode, fallbackMessage) {
      const keyByCode = {
        AI_ROLE_REQUIRED: "roleRequired",
        AI_PROVIDER_CREDENTIAL_REQUIRED: "credentialRequired",
        AI_CREDENTIAL_DECRYPT_FAILED: "credentialDecryptFailed",
        AI_PROVIDER_OPENAI_RUN_FAILED: "providerFailed",
        AI_PROVIDER_OPENAI_INVALID_JSON: "invalidOutput",
        AI_CV_PARSE_FAILED: "providerFailed",
        AI_CV_PROVIDER_INVALID: "configurationInvalid",
        AI_CV_PROMPT_INACTIVE: "configurationInvalid",
        AI_CV_RUN_START_FAILED: "serviceUnavailable",
        AI_CV_TEXT_REQUIRED: "fileUnsupported",
        AI_CV_TEXT_TOO_LARGE: "fileTooLarge",
        CV_FILE_TYPE_UNSUPPORTED: "fileUnsupported",
        AI_INTERNAL_ERROR: "serviceUnavailable",
        AI_INTERNAL_TOKEN_MISSING: "serviceUnavailable",
        AI_INTERNAL_TOKEN_INVALID: "serviceUnavailable",
        AI_SERVICE_UNCONFIGURED: "serviceUnavailable",
        AI_SERVICE_REQUEST_FAILED: "serviceUnavailable",
        AI_CV_READINESS_FAILED: "readinessFailed",
      };
      const key = keyByCode[errorCode];
      return key ? this.$t(`crm.uploadCv.errors.${key}`) : fallbackMessage || this.$t("crm.uploadCv.error");
    },
  },
};
</script>
